import {
  type GetAssemblyAiTokenMutation,
  UtSessionCommandType,
} from '@/survey-graphql';

import { VOICE_COMMAND_TYPE_MAP } from './shared-survey-mobile-ut-phone.consts';
import type { MobileUtSessionMutations } from './shared-survey-mobile-ut-phone.types';
import { SPEECH_TO_TEXT_TOKEN_REQUEST } from './shared-survey-project-response-mode.consts';

interface VoiceCommandPayload {
  type: string;
  transcript: string;
  triggerWord: string;
  confidence: number;
}

interface TranscriptPayload {
  transcript: string;
  confidence: number;
  isFinal: boolean;
}

interface WorkerListenerDeps {
  uniqueId: string;
  submitTranscript: MobileUtSessionMutations['submitTranscript'];
  getAssemblyAIToken: MobileUtSessionMutations['getAssemblyAIToken'];
  getWorker: () => Worker | null;
  claimTranscriptUpdate: (isFinal: boolean) => boolean;
  onVoiceFailure: () => void;
}

export function requestMobileUtAssemblyToken(
  getAssemblyAIToken: MobileUtSessionMutations['getAssemblyAIToken']
) {
  return getAssemblyAIToken({
    variables: {
      input: SPEECH_TO_TEXT_TOKEN_REQUEST,
    },
  });
}

export function postMobileUtStartListening(
  worker: Worker,
  assemblyAIToken: GetAssemblyAiTokenMutation['assemblyAIToken'],
  sampleRate: number
) {
  /* oxlint-disable unicorn/require-post-message-target-origin */
  worker.postMessage({
    id: `start_${Date.now()}`,
    type: 'START_LISTENING',
    payload: {
      token: assemblyAIToken.token,
      sampleRate: sampleRate ?? 44100,
      format: 'pcm16',
      channels: 1,
      // Reconnect AssemblyAI WS after each emit to dodge multilingual-model post-turn cooldown.
      reconnectAfterEmit: true,
      // AssemblyAI tokens lapse at ≤600s while a UT runs far longer; let the worker ask for a
      // fresh token on reconnect/1008 instead of dying mid-session.
      refreshTokenOnReconnect: true,
      expiresAt: assemblyAIToken.expiresAt ?? undefined,
    },
  });
  /* oxlint-enable unicorn/require-post-message-target-origin */
}

function submitVoiceCommand(
  { uniqueId, submitTranscript }: WorkerListenerDeps,
  payload: VoiceCommandPayload
) {
  const mappedType = VOICE_COMMAND_TYPE_MAP[payload.type];
  if (!mappedType) return;

  submitTranscript({
    variables: {
      input: {
        uniqueId,
        transcript: payload.transcript,
        triggerWord: payload.triggerWord,
        type: mappedType,
        confidence: payload.confidence,
        isFinal: true,
      },
    },
  }).catch((error: unknown) => {
    console.error('[mobile-ut] voice command submit failed', error);
  });
}

function submitTranscriptUpdate(
  { uniqueId, submitTranscript, claimTranscriptUpdate }: WorkerListenerDeps,
  payload: TranscriptPayload
) {
  if (!claimTranscriptUpdate(payload.isFinal)) return;

  submitTranscript({
    variables: {
      input: {
        uniqueId,
        transcript: payload.transcript,
        triggerWord: null,
        type: UtSessionCommandType.TranscriptUpdate,
        confidence: payload.confidence,
        isFinal: payload.isFinal,
      },
    },
  }).catch(() => undefined);
}

function refreshWorkerToken({
  getAssemblyAIToken,
  getWorker,
}: WorkerListenerDeps) {
  requestMobileUtAssemblyToken(getAssemblyAIToken)
    .then(({ data }) => {
      getWorker()?.postMessage({
        id: `token_${Date.now()}`,
        type: 'TOKEN_REFRESHED',
        payload: {
          token: data?.assemblyAIToken?.token ?? null,
          expiresAt: data?.assemblyAIToken?.expiresAt ?? null,
        },
      });
    })
    .catch(() => {
      getWorker()?.postMessage({
        id: `token_${Date.now()}`,
        type: 'TOKEN_REFRESHED',
        payload: { token: null, expiresAt: null },
      });
    });
}

export function attachMobileUtWorkerListeners(
  worker: Worker,
  deps: WorkerListenerDeps
) {
  worker.addEventListener('message', (e) => {
    const { type, payload } = e.data;

    if (type === 'VOICE_COMMAND') {
      submitVoiceCommand(deps, payload);
    }

    if (type === 'TRANSCRIPT') {
      submitTranscriptUpdate(deps, payload);
    }

    if (type === 'REQUEST_TOKEN') {
      refreshWorkerToken(deps);
    }

    if (type === 'ERROR') {
      console.error('[mobile-ut] voice transcription error:', payload.error);
      deps.onVoiceFailure();
    }
  });

  worker.addEventListener('error', () => {
    console.error('[mobile-ut] voice worker error');
    deps.onVoiceFailure();
  });
}
